import {
  createFileRoute,
  FileRouteTypes,
  Link,
  Outlet,
} from "@tanstack/react-router";
import {
  CommandLineIcon,
  ArrowRightStartOnRectangleIcon,
  AcademicCapIcon,
  BanknotesIcon,
  BookOpenIcon,
  ChartBarIcon,
  FolderIcon,
  ArrowUpRightIcon,
  MapIcon,
  KeyIcon,
  Cog8ToothIcon,
  ClipboardDocumentListIcon,
  InformationCircleIcon,
  HomeIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  PlusIcon,
  LanguageIcon,
  MicrophoneIcon,
  PencilSquareIcon,
  BeakerIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";

import { UserIcon } from "@heroicons/react/24/solid";
import faviconUrl from "../../../favicon.ico?url";

import {
  useLoginStatus,
  AuthStatusError,
  type LoginData,
} from "../../hooks/useLoginStatus";
import { LoginComponent } from "../../components/LoginComponent";
import LoadingScreen from "../../components/LoadingScreen";
import AuthErrorScreen from "../../components/AuthErrorScreen";
import {
  CSSProperties,
  JSX,
  useEffect,
  useRef,
  useState,
} from "react";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useLogin } from "../../hooks/useLogin";
import useTranslation from "../../hooks/useTranslation";
import { SnackBar } from "../../components/SnackBar";
import ProgressBar from "../../components/ProgressBar";
import { useUserProfileSendHeartbeat } from "../../hooks/useUserProfileSendHeartbeat";
import { useFrontendRefreshOnNewVersion } from "../../hooks/useFrontendRefreshOnNewVersion";
import {
  useLanguageSpace,
  type LanguageSpace,
} from "../../hooks/useLanguageSpace";
import { ENABLED_LANGUAGE_SPACE_IDS, TEXT_LANGUAGES } from "../../constants";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  deriveEffectiveLanguageSpaceScope,
  getAddedLanguageSpacesStorageKey,
} from "../../hooks/useAddedLanguageSpaces";

import { useNavigate, useRouterState } from "@tanstack/react-router";
import { PATTERN_UNLOCKS_MVP_VISIBLE } from "../../features/patternUnlocks/patternUnlockVisibility";
import { useRabbitHoleWeeksList, type RabbitHoleWeek } from "../../hooks/useRabbitHole";
import NotFoundScreen from "../../components/NotFoundScreen";

export const Route = createFileRoute("/app")({
  component: AppLayout,
  notFoundComponent: NotFoundScreen,
});

function AppLayout() {
  const loginStatus = useLoginStatus();

  if (__BUILD_VERSION__ === "dev") {
    const errorKind =
      loginStatus.error instanceof AuthStatusError
        ? (loginStatus.error as AuthStatusError).kind
        : undefined;
    console.log("[auth] AppLayout rendering", {
      isSuccess: loginStatus.isSuccess,
      isPending: loginStatus.isPending,
      isError: loginStatus.isError,
      fetchStatus: loginStatus.fetchStatus,
      errorKind,
      authState: loginStatus.data?.state,
    });
  }

  if (loginStatus.isSuccess) {
    if (loginStatus.data.state === "logged out") {
      return <LoginComponent />;
    }
    return <AppLayoutImpl />;
  }

  if (loginStatus.fetchStatus === "paused") {
    return (
      <AuthErrorScreen
        type="offline"
        onRetry={() => loginStatus.refetch()}
      />
    );
  }

  if (loginStatus.isPending) {
    return <LoadingScreen />;
  }

  return (
    <AuthErrorScreen
      error={loginStatus.error}
      onRetry={() => loginStatus.refetch()}
    />
  );
}

type LinkTarget = FileRouteTypes["to"];

interface NavItem {
  link: LinkTarget;
  text: string;
  pageTitle?: string;
  icon: JSX.Element;
  testId?: string;
}

type AppSpaceOptionId = LanguageSpace | "OVERVIEW";
type AppSpaceOption = {
  id: AppSpaceOptionId;
  label: string;
  shortLabel: string;
  accentBg: string;
  accentText: string;
};

type MobileLanguagePageTheme = {
  surface: string;
  border: string;
  ink: string;
  wash: string;
  pageWash: string;
  contentWash: string;
};

const LANGUAGE_SPACE_ENDONYM_KEYS: Partial<
  Record<LanguageSpace, string>
> = {
  ENGLISH: "mobile_language_space_endonym_english",
  GERMAN: "mobile_language_space_endonym_german",
  FRENCH: "mobile_language_space_endonym_french",
  ITALIAN: "mobile_language_space_endonym_italian",
};

const MOBILE_LANGUAGE_PAGE_THEME_BY_SPACE: Partial<
  Record<LanguageSpace, MobileLanguagePageTheme>
> = {
  ENGLISH: {
    surface: "rgba(231, 236, 225, 0.82)",
    border: "rgba(190, 199, 185, 0.72)",
    ink: "#2f3b35",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(213,220,206,0.8) 40%, rgba(181,192,183,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(210,219,206,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(192,203,193,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
  FRENCH: {
    surface: "rgba(248, 234, 208, 0.84)",
    border: "rgba(226, 190, 152, 0.82)",
    ink: "#7f4328",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(239,219,189,0.8) 40%, rgba(228,190,157,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(236,211,178,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(228,195,154,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
  GERMAN: {
    surface: "rgba(252, 242, 220, 0.84)",
    border: "rgba(222, 200, 160, 0.82)",
    ink: "#7b4a1e",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(238,215,177,0.8) 40%, rgba(218,186,143,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(238,215,177,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(218,186,143,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
  ITALIAN: {
    surface: "rgba(233, 226, 242, 0.84)",
    border: "rgba(192, 176, 220, 0.82)",
    ink: "#5a4b83",
    wash: "linear-gradient(180deg, rgba(245,241,228,0.96) 0%, rgba(218,208,234,0.8) 40%, rgba(189,177,219,0.46) 72%, rgba(248,250,252,0.14) 100%), radial-gradient(circle at 78% 10%, rgba(255,250,234,0.34) 0%, rgba(255,250,234,0) 28%)",
    pageWash:
      "linear-gradient(180deg, rgba(244,240,228,0.8) 0px, rgba(208,196,228,0.34) 182px, rgba(248,250,252,0.96) 430px), radial-gradient(circle at 78% 0%, rgba(255,250,234,0.24) 0%, rgba(255,250,234,0) 28%)",
    contentWash:
      "linear-gradient(180deg, rgba(237,232,226,0.34) 0px, rgba(197,183,223,0.18) 130px, rgba(248,250,252,0) 300px)",
  },
};

function getLanguageSpaceEndonymLabel(
  id: LanguageSpace,
  fallbackLabel: string,
  translate: (key: string) => string,
) {
  const key = LANGUAGE_SPACE_ENDONYM_KEYS[id];
  return key ? translate(key) : fallbackLabel;
}

function getMobileLanguagePageTheme(
  id: LanguageSpace | undefined,
  fallbackAccentBg: string | undefined,
  fallbackAccentText: string | undefined,
): MobileLanguagePageTheme {
  if (id && MOBILE_LANGUAGE_PAGE_THEME_BY_SPACE[id]) {
    return MOBILE_LANGUAGE_PAGE_THEME_BY_SPACE[id];
  }

  return {
    surface: fallbackAccentBg ?? "#F8FAFC",
    border: "#E2E8F0",
    ink: fallbackAccentText ?? "#0F172A",
    wash: "linear-gradient(180deg, rgba(241,245,249,0.94) 0%, rgba(229,235,241,0.74) 34%, rgba(214,223,233,0.32) 76%, rgba(248,250,252,0.12) 100%)",
    pageWash:
      "linear-gradient(180deg, rgba(241,245,249,0.78) 0px, rgba(226,232,240,0.3) 180px, rgba(248,250,252,0.96) 430px)",
    contentWash:
      "linear-gradient(180deg, rgba(241,245,249,0.28) 0px, rgba(226,232,240,0.14) 128px, rgba(248,250,252,0) 300px)",
  };
}

function resolveAppSpaceLabel(
  option: Pick<AppSpaceOption, "id" | "label">,
  translate: (key: string) => string,
) {
  if (option.id === "OVERVIEW") {
    return translate("sidenav_overview");
  }
  if (option.id === "GREEK_LATIN_TRANSCRIPTION") {
    return translate("language_greek");
  }
  if (option.id === "RUSSIAN_LATIN_TRANSCRIPTION") {
    return translate("language_russian");
  }
  if (
    option.id === "PORTUGUESE_FROM_BRAZIL" ||
    option.id === "PORTUGUESE_FROM_PORTUGAL"
  ) {
    return translate("language_portuguese");
  }
  return option.label;
}

function AppLayoutImpl() {
  const loginStatus = useLoginStatus();
  const userData = loginStatus.data as LoginData;
  const userSettings = useUserSettings();
  const navigate = useNavigate();
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const search = useRouterState({
    select: (state) => state.location.searchStr,
  });
  const isSettingsPage =
    pathname === "/app/space_settings" || pathname === "/app/settings";
  const cameFromSpaceSettings = search.includes("from=space_settings");
  const isInSettingsFlow = isSettingsPage || cameFromSpaceSettings;
  const isOnTextDetailsPage =
    pathname.startsWith("/app/reeprise/") && pathname !== "/app/reeprise";
  const isOnStartLearningPage = pathname === "/app/start-learning";
  const isOnMainAppPage = pathname === "/app" || pathname === "/app/reeprise";
  const showMobileLanguagePageNavigation =
    pathname === "/app" || pathname === "/app/reeprise" || pathname === "/app/rabbit_hole";
  const showMobileLanguagePageNavigationShellWash = pathname === "/app/reeprise";
  const isOnMobileCreateView =
    pathname === "/app/reeprise" && search.includes("mobileView=create");
  const activeRabbitHoleWeekId =
    pathname === "/app/rabbit_hole" && search
      ? (() => {
          const match = search.match(/[?&]weekId=(\d+)/);
          return match ? Number(match[1]) : null;
        })()
      : null;
  const showDesktopSideNav = !isOnTextDetailsPage;
  const desktopSidebarExpanded = true;
  const showMobileBottomNav =
    pathname === "/app/reeprise" ||
    pathname === "/app/quick_translate" ||
    pathname === "/app/vocabulary_progress" ||
    pathname === "/app/profile";
  const hideNavbarAuxControls = isOnTextDetailsPage;
  const showLanguageSpaceSwitch =
    !isInSettingsFlow && !isOnTextDetailsPage && !isOnStartLearningPage;
  const mobileBackTarget = cameFromSpaceSettings
    ? "/app/space_settings"
    : isSettingsPage
      ? "/app/profile"
      : "/app";
  useFrontendRefreshOnNewVersion();
  const { mutateAsync: sendHeartbeat } = useUserProfileSendHeartbeat();
  const login = useLogin();

  const translation = useTranslation();

  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const userAddedLanguageSpacesStorageKey = getAddedLanguageSpacesStorageKey(
    loginStatus.data?.state === "logged in" ? userData.userId : null,
  );
  const [addedLanguageSpaces] = useLocalStorage<string[]>(
    userAddedLanguageSpacesStorageKey,
    [],
  );
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const serverAddedLanguageSpaces = Array.isArray(
    userSettings.data?.added_language_spaces,
  )
    ? userSettings.data.added_language_spaces
    : [];
  const hasPersistedServerAddedLanguageSpaces =
    serverAddedLanguageSpaces.length > 0;
  const onboardingCompleted = userSettings.data?.onboarding_completed === true;
  const hasPersistedLocalAddedLanguageSpaces =
    typeof window !== "undefined" &&
    window.localStorage.getItem(userAddedLanguageSpacesStorageKey) !== null;
  const hasPersistedAddedLanguageSpaces =
    hasPersistedServerAddedLanguageSpaces || hasPersistedLocalAddedLanguageSpaces;
  const effectiveAddedLanguageSpaces =
    hasPersistedServerAddedLanguageSpaces
      ? serverAddedLanguageSpaces
      : addedLanguageSpaces;
  const isForcedStartLearningOnboarding =
    isOnStartLearningPage &&
    (userSettings.isSuccess ? !onboardingCompleted : true);
  const shouldShowTopbarBackButton =
    !isOnMainAppPage && !isForcedStartLearningOnboarding;
  const {
    effectiveLanguageSpaceIds,
    effectiveLanguageSpaceSet,
    hasAddedAllEnabledSpaces,
  } = deriveEffectiveLanguageSpaceScope({
    addedLanguageSpaces: effectiveAddedLanguageSpaces,
    enabledLanguageSpaceIds: ENABLED_LANGUAGE_SPACE_IDS,
    hasPersistedAddedLanguageSpaces,
    fallbackLanguageSpaceId: languageSpace === "ALL" ? "ENGLISH" : languageSpace,
  });
  const languageOptions = TEXT_LANGUAGES
    .filter((lang) => effectiveLanguageSpaceSet.has(lang.id))
    .map((lang) => ({
      id: lang.id as LanguageSpace,
      label: translation.translate(lang.prettyTranslationKey),
      shortLabel: lang.short,
      accentBg: lang.accentBg,
      accentText: lang.accentText,
    }));
  const overviewSpaceOption: AppSpaceOption = {
    id: "OVERVIEW" as const,
    label: translation.translate("sidenav_overview"),
    shortLabel: "OV",
    accentBg: "#F8FAFC",
    accentText: "#0F172A",
  };
  const appSpaceOptions: AppSpaceOption[] = [
    ...languageOptions.slice(0, 1),
    overviewSpaceOption,
    ...languageOptions.slice(1),
  ];
  const activeAppSpaceId: AppSpaceOptionId =
    pathname === "/app" ? "OVERVIEW" : languageSpace;
  const currentLanguageIndex = languageOptions.findIndex(
    (opt) => opt.id === languageSpace,
  );
  const currentLanguage = languageOptions[currentLanguageIndex];
  const currentLanguageLabel = resolveAppSpaceLabel(
    activeAppSpaceId === "OVERVIEW"
      ? overviewSpaceOption
      : currentLanguage ?? overviewSpaceOption,
    translation.translate,
  );
  useEffect(() => {
    let stopped = false;
    (async () => {
      while (!stopped) {
        try {
          // only send the heartbeat if the document is visible
          if (document.visibilityState !== "visible") {
            await new Promise((resolve) => setTimeout(resolve, 5_000));
            continue;
          }
          await sendHeartbeat();
        } catch (error) {
          console.error("Failed to send heartbeat:", error);
        }
        await new Promise((resolve) => setTimeout(resolve, 30_000));
      }
    })();
    return () => {
      stopped = true;
    };
  }, [sendHeartbeat]);

  useEffect(() => {
    if (!languageMenuOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      if (!languageMenuRef.current) return;
      if (!languageMenuRef.current.contains(event.target as Node)) {
        setLanguageMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [languageMenuOpen]);

  useEffect(() => {
    if (hideNavbarAuxControls) {
      setLanguageMenuOpen(false);
    }
  }, [hideNavbarAuxControls]);

  useEffect(() => {
    const ids = languageOptions.map((opt) => opt.id);
    if (ids.length === 0) {
      return;
    }
    if (!ids.includes(languageSpace)) {
      const fallbackSpace = ids[0];
      if (fallbackSpace) {
        setLanguageSpace(fallbackSpace);
      }
    }
  }, [languageOptions, languageSpace, setLanguageSpace]);

  useEffect(() => {
    if (pathname === "/app/start-learning") {
      return;
    }
    if (!userSettings.isSuccess) {
      return;
    }
    if (userSettings.data.onboarding_completed !== false) {
      return;
    }
    void navigate({ to: "/app/start-learning" });
  }, [navigate, pathname, userSettings.data, userSettings.isSuccess]);

  useEffect(() => {
    if (
      !hasPersistedAddedLanguageSpaces ||
      effectiveLanguageSpaceIds.length === 0 ||
      effectiveLanguageSpaceSet.has(languageSpace)
    ) {
      return;
    }
    const fallbackSpace = effectiveLanguageSpaceIds[0];
    if (!fallbackSpace) {
      return;
    }
    setLanguageSpace(fallbackSpace as LanguageSpace);
  }, [
    effectiveLanguageSpaceIds,
    effectiveLanguageSpaceSet,
    hasPersistedAddedLanguageSpaces,
    languageSpace,
    setLanguageSpace,
  ]);

  const show_experimental =
    userData.is_staff &&
    userSettings.data?.show_experimental_features === true;
  const activeMobileLanguage =
    appSpaceOptions.find((opt) => opt.id === activeAppSpaceId) ??
    currentLanguage ??
    overviewSpaceOption;
  const activeMobileLanguageTheme = getMobileLanguagePageTheme(
    activeMobileLanguage?.id === "OVERVIEW"
      ? undefined
      : activeMobileLanguage?.id,
    activeMobileLanguage?.accentBg,
    activeMobileLanguage?.accentText,
  );
  const navigateToAppSpace = (spaceId: AppSpaceOptionId) => {
    if (spaceId === "OVERVIEW") {
      void navigate({ to: "/app" });
      return;
    }
    setLanguageSpace(spaceId);
    const isOnSpaceAwarePage = pathname === "/app/reeprise" || pathname === "/app/rabbit_hole";
    if (!isOnSpaceAwarePage || search.includes("mobileView=create")) {
      void navigate({
        to: "/app/reeprise",
        search: { mobileView: undefined },
      });
    }
  };

  const handleBrandClick = () => {
    setLanguageMenuOpen(false);
    navigate({ to: "/app" });
  };
  const closeNavbarOnMobile = () => {
    if (typeof window === "undefined") {
      return;
    }
    if (!window.matchMedia("(max-width: 767px)").matches) {
      return;
    }
    setLanguageMenuOpen(false);
  };
  const navigateBackFromTopbar = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    navigate({ to: mobileBackTarget });
  };
  const openAddLanguageSpaceFlow = () => {
    setLanguageMenuOpen(false);
    void navigate({
      to: "/app/start-learning",
    });
  };
  const mobileBottomNavItems = [
    {
      key: "overview",
      label: translation.translate("sidenav_overview"),
      icon: <HomeIcon className="h-5 w-5" />,
      active: pathname === "/app",
      onClick: () => {
        void navigate({
          to: "/app",
        });
      },
      testId: "mobile-bottom-nav-overview",
    },
    {
      key: "texts",
      label: translation.translate("sidenav_texts"),
      icon: <BookOpenIcon className="h-5 w-5" />,
      active: pathname === "/app/reeprise" && !isOnMobileCreateView,
      onClick: () => {
        void navigate({
          to: "/app/reeprise",
          search: { mobileView: undefined },
        });
      },
      testId: "mobile-bottom-nav-texts",
    },
    {
      key: "progress",
      label: translation.translate("mobile_nav_word_archive"),
      icon: <FolderIcon className="h-5 w-5" />,
      active: pathname === "/app/vocabulary_progress",
      onClick: () => {
        void navigate({
          to: "/app/vocabulary_progress",
          search: { generatedTextId: undefined, tab: undefined },
        });
      },
      testId: "mobile-bottom-nav-progress",
    },
    {
      key: "quick-translate",
      label: translation.translate("quick_translate_label"),
      icon: <PencilSquareIcon className="h-5 w-5" />,
      active: pathname === "/app/quick_translate",
      onClick: () => {
        void navigate({ to: "/app/quick_translate" });
      },
      testId: "mobile-bottom-nav-quick-translate",
    },
    {
      key: "profile",
      label: translation.translate("profile_title"),
      icon: <UserIcon className="h-5 w-5" />,
      active: pathname === "/app/profile",
      onClick: () => {
        void navigate({ to: "/app/profile" });
      },
      testId: "mobile-bottom-nav-profile",
    },
  ] as const;
  const primaryDesktopNavOptions = [
    overviewSpaceOption,
    ...languageOptions,
  ] as AppSpaceOption[];

  const MAX_VISIBLE_WEEKS = 5;
  const [expandedLanguageFolders, setExpandedLanguageFolders] = useState<
    Set<string>
  >(new Set());
  const [showAllWeeksForLanguage, setShowAllWeeksForLanguage] = useState<
    Set<string>
  >(new Set());

  const allRabbitHoleWeeks = useRabbitHoleWeeksList();

  const handleToggleFolder = (spaceId: AppSpaceOptionId) => {
    if (spaceId === "OVERVIEW") return;
    setExpandedLanguageFolders((prev) => {
      const next = new Set(prev);
      if (next.has(spaceId)) {
        next.delete(spaceId);
      } else {
        next.add(spaceId);
        setLanguageSpace(spaceId);
      }
      return next;
    });
  };  return (
    <div
      className="relative flex h-dvh w-full flex-col overflow-hidden bg-neutral-50"
      style={
        {
          "--app-desktop-nav-width": showDesktopSideNav
            ? desktopSidebarExpanded
              ? "240px"
              : "64px"
            : "0px",
          ...(showMobileLanguagePageNavigationShellWash
            ? { background: activeMobileLanguageTheme.pageWash }
            : {}),
        } as CSSProperties
      }
    >
      <div
        className={`relative z-40 items-center justify-between border border-slate-200 px-3 md:pr-4 md:pl-0 ${
          showMobileLanguagePageNavigation ? "hidden" : "flex md:hidden h-16"
        }`}
        style={
          showMobileLanguagePageNavigationShellWash
            ? { backgroundColor: "rgba(248,250,252,0.34)" }
            : undefined
        }
      >
        {showDesktopSideNav ? (
          <div className="hidden h-16 items-center justify-center md:flex md:w-[64px] md:shrink-0 md:pl-0" />
        ) : null}
        <div className="flex items-center gap-2 md:hidden">
          {shouldShowTopbarBackButton ? (
            <button
              type="button"
              aria-label="Go back"
              onClick={navigateBackFromTopbar}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
          ) : null}
        </div>
        <div className="flex items-center gap-3 h-16 md:pr-4">
          <div
            className={`relative ${showLanguageSpaceSwitch ? "" : "hidden"} md:hidden ${
              showMobileLanguagePageNavigation ? "hidden" : ""
            }`}
            ref={languageMenuRef}
          >
            <button
              type="button"
              data-testid="topbar-language-space-switch"
              aria-label={translation.translate("language_space_switch")}
              onClick={() => setLanguageMenuOpen((prev) => !prev)}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            >
              <span className="max-w-[8.5rem] truncate">{currentLanguageLabel}</span>
              <ChevronDownIcon
                className={`h-4 w-4 text-slate-500 transition ${
                  languageMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {languageMenuOpen && (
              <div className="absolute right-0 z-50 mt-2 w-60 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-slate-200 bg-white p-2 max-h-[70vh] overflow-y-auto">
                <div className="px-3 py-1 text-base uppercase tracking-wide text-slate-500">
                  {translation.translate("language_space_switch")}
                </div>
                <div className="mt-1 grid gap-1">
                  {appSpaceOptions.map((opt) => {
                    const isActive = opt.id === activeAppSpaceId;
                    const isOverview = opt.id === "OVERVIEW";
                    const isGreekTranscript =
                      !isOverview && opt.id === "GREEK_LATIN_TRANSCRIPTION";
                    const isRussianTranscript =
                      !isOverview && opt.id === "RUSSIAN_LATIN_TRANSCRIPTION";
                    const isPortugueseVariant =
                      !isOverview &&
                      (opt.id === "PORTUGUESE_FROM_BRAZIL" ||
                        opt.id === "PORTUGUESE_FROM_PORTUGAL");
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => {
                          navigateToAppSpace(opt.id);
                          setLanguageMenuOpen(false);
                        }}
                        className="flex items-center justify-start gap-3 rounded-xl px-3 py-2 text-base font-semibold text-left transition"
                        style={{
                          backgroundColor: isActive ? "#e2e8f0" : "#f8fafc",
                          color: "#0f172a",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <span className="flex flex-col items-start leading-tight">
                          <span>{resolveAppSpaceLabel(opt, translation.translate)}</span>
                          {(isGreekTranscript || isRussianTranscript) && (
                            <span className="text-base text-slate-500">
                              {translation.translate(
                                "language_variant_latin_transcription",
                              )}
                            </span>
                          )}
                          {isPortugueseVariant && (
                            <span className="text-base text-slate-500">
                              {translation.translate(
                                opt.id === "PORTUGUESE_FROM_BRAZIL"
                                  ? "language_variant_portuguese_brazil"
                                  : "language_variant_portuguese_portugal",
                              )}
                            </span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                  {!hasAddedAllEnabledSpaces ? (
                    <button
                      type="button"
                      data-testid="add-language-space-option"
                      onClick={openAddLanguageSpaceFlow}
                      className="flex items-center justify-start gap-3 rounded-xl px-3 py-2 text-base font-semibold text-left text-slate-900 transition hover:bg-slate-50"
                      style={{
                        border: "1px dashed #cbd5e1",
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <span>{translation.translate("space_settings_add_new_space")}</span>
                    </button>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showMobileLanguagePageNavigation ? (
        <nav
          aria-label={translation.translate("language_space_switch")}
          data-testid="mobile-language-space-page-navigation"
          className="relative overflow-hidden border-x border-b border-slate-200 px-3 py-1.5 md:hidden"
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 100%)",
            }}
          />
          <div className="relative flex items-center justify-center gap-2">
            {[overviewSpaceOption, ...languageOptions].map((opt) => {
              const pageLabel =
                opt.id === "OVERVIEW"
                  ? translation.translate("sidenav_overview")
                  : getLanguageSpaceEndonymLabel(
                      opt.id,
                      opt.label,
                      translation.translate,
                    );
              const isActive = opt.id === activeAppSpaceId;
              const pageTheme = getMobileLanguagePageTheme(
                opt.id === "OVERVIEW" ? undefined : opt.id,
                opt.accentBg,
                opt.accentText,
              );
              return (
                <button
                  key={opt.id}
                  type="button"
                  data-testid={`mobile-language-space-page-${opt.id.toLowerCase()}`}
                  data-state={isActive ? "active" : "inactive"}
                  aria-label={pageLabel}
                  aria-current={isActive ? "page" : undefined}
                  onClick={() => {
                    if (!isActive) {
                      navigateToAppSpace(opt.id);
                    }
                  }}
                  className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                  style={{ color: pageTheme.ink }}
                >
                  {opt.id === "OVERVIEW" ? (
                    <span
                      data-testid="mobile-language-space-overview-cover"
                      className="relative block h-9 w-[30px]"
                      aria-hidden="true"
                    >
                      <span className="absolute left-[6px] top-0 h-8 w-6 rounded-[2px] border border-[#d8cbc2] bg-[#f4ece6]" />
                      <span className="absolute left-[3px] top-[2px] h-8 w-6 rounded-[2px] border border-[#d5c7bd] bg-[#f1e8e1]" />
                      <span
                        className="absolute left-0 top-[4px] h-8 w-6 rounded-[2px] border border-[#d2c3b8] bg-[#eee5de]"
                      >
                        {isActive ? (
                          <span className="absolute inset-x-[-1px] bottom-[-1px] h-0.5 bg-slate-900" />
                        ) : null}
                      </span>
                    </span>
                  ) : (
                    <span
                      data-testid={`mobile-language-space-cover-${opt.id.toLowerCase()}`}
                      className="relative inline-flex h-9 w-[30px] items-center justify-center rounded-[2px] border font-TikTok text-[10px] font-medium tracking-[0.04em] transition-colors"
                      style={{
                        backgroundColor: pageTheme.surface,
                        borderColor: pageTheme.border,
                      }}
                      aria-hidden="true"
                    >
                      {opt.shortLabel}
                      {isActive ? (
                        <span className="absolute inset-x-[-1px] bottom-[-1px] h-0.5 bg-slate-900" />
                      ) : null}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>
      ) : null}
      {showDesktopSideNav ? (
        <div
          data-testid="topbar-sidenav-divider"
          className={`pointer-events-none fixed bottom-0 top-0 z-[70] hidden w-px bg-slate-200 transition-[left] duration-200 md:block ${
            desktopSidebarExpanded ? "left-[240px]" : "left-[64px]"
          }`}
        />
      ) : null}

      <div
        className="relative flex flex-1 min-h-0"
      >
        {showDesktopSideNav ? (
          <div
            data-testid="side-navigation-shell"
            className={`hidden md:flex md:h-full md:min-h-0 md:shrink-0 md:overflow-hidden md:transition-[width] ${
              desktopSidebarExpanded ? "md:w-[240px]" : "md:w-[64px]"
            }`}
          >
            <div
              data-testid="side-navigation-panel"
              className={`side-nav-hover-scrollbar h-full min-h-0 w-full overflow-y-auto overscroll-contain bg-white md:flex md:min-h-0 md:flex-col md:border-b md:border-l md:border-slate-200 ${
                desktopSidebarExpanded
                  ? "pointer-events-auto md:w-[240px]"
                  : "md:w-[64px]"
              }`}
            >
              <div className="space-y-3 px-2 py-3">
                <div className="border-b border-slate-200 pb-2">
                  <button
                    type="button"
                    data-testid="navbar-toggle"
                    aria-label={translation.translate("website_nav_menu_toggle")}
                    onClick={handleBrandClick}
                    className="flex w-full items-center justify-start rounded-md px-3 py-1.5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <span className="inline-flex items-center gap-2">
                      <img
                        data-testid="sidebar-brand-favicon"
                        src={faviconUrl}
                        alt=""
                        className="h-5 w-5 shrink-0"
                      />
                      <span className="text-sm font-medium leading-5">Uxhulu</span>
                    </span>
                  </button>
                </div>
                <div className="space-y-1 border-b border-slate-200 pb-3">
                  <div className="px-3 pt-1">
                    <p className="text-[11px] font-medium leading-4 text-slate-500">
                      {translation.translate("side_nav_learning_spaces")}
                    </p>
                  </div>
                  <div
                    data-testid="desktop-side-nav-spaces"
                    className="grid gap-0.5"
                  >
                    {primaryDesktopNavOptions.map((opt) => {
                      const isActive = opt.id === activeAppSpaceId;
                      const isLanguageActive =
                        isActive && activeRabbitHoleWeekId === null;
                      const isExpanded =
                        opt.id !== "OVERVIEW" &&
                        expandedLanguageFolders.has(opt.id);
                      const optionLabel =
                        opt.id === "OVERVIEW"
                          ? resolveAppSpaceLabel(opt, translation.translate)
                          : getLanguageSpaceEndonymLabel(
                              opt.id,
                              opt.label,
                              translation.translate,
                            );
                      const optionIcon =
                        opt.id === "OVERVIEW" ? (
                          <HomeIcon className="h-4 w-4" />
                        ) : (
                          <FolderIcon className="h-4 w-4" />
                        );
                      const optionTestId =
                        opt.id === "OVERVIEW"
                          ? "desktop-side-nav-space-overview"
                          : `desktop-side-nav-space-${opt.id.toLowerCase()}`;
                      const isLanguage = opt.id !== "OVERVIEW";
                      const weeks =
                        isExpanded && allRabbitHoleWeeks.data
                          ? allRabbitHoleWeeks.data.filter(
                              (w) => w.language === opt.id,
                            )
                          : [];
                      const showAll = showAllWeeksForLanguage.has(opt.id);
                      const visibleWeeks = showAll
                        ? weeks
                        : weeks.slice(0, MAX_VISIBLE_WEEKS);
                      const hiddenCount =
                        !showAll && weeks.length > MAX_VISIBLE_WEEKS
                          ? weeks.length - MAX_VISIBLE_WEEKS
                          : 0;

                      return (
                        <div key={opt.id}>
                          <div
                            className="flex w-full items-center gap-2 rounded-md text-left transition hover:bg-slate-50"
                            style={{ backgroundColor: isLanguageActive ? "#F1F5F9" : "transparent" }}
                          >
                            <button
                              type="button"
                              data-testid={optionTestId}
                              aria-label={optionLabel}
                              title={optionLabel}
                              onClick={() => {
                                if (isLanguage) {
                                  setExpandedLanguageFolders((prev) =>
                                    new Set([...prev, opt.id]),
                                  );
                                  setLanguageSpace(opt.id);
                                  if (
                                    pathname !== "/app/reeprise" ||
                                    languageSpace !== opt.id
                                  ) {
                                    void navigate({
                                      to: "/app/reeprise",
                                      search: { mobileView: undefined },
                                    });
                                  }
                                } else {
                                  navigateToAppSpace(opt.id);
                                }
                              }}
                              className="flex min-w-0 flex-1 items-center gap-2 px-3 py-1.5 text-left"
                            >
                              <span className="inline-flex h-4 w-4 items-center justify-center text-slate-500">
                                {optionIcon}
                              </span>
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-medium leading-5 text-slate-700">
                                  {optionLabel}
                                </span>
                              </span>
                            </button>
                            {isLanguage ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFolder(opt.id);
                                }}
                                className="flex shrink-0 items-center justify-center px-2 py-1.5 text-slate-400 hover:text-slate-600"
                                data-testid={`side-nav-space-chevron-${opt.id.toLowerCase()}`}
                                aria-label={
                                  isExpanded
                                    ? translation.translate("overview_rabbit_hole_collapse")
                                    : translation.translate("overview_rabbit_hole_expand")
                                }
                              >
                                <ChevronDownIcon
                                  className={`h-3.5 w-3.5 transition-transform ${isExpanded ? "" : "-rotate-90"}`}
                                />
                              </button>
                            ) : null}
                          </div>
                          {isExpanded ? (
                            <div
                              className="ml-7 grid gap-0.5"
                              data-testid={`side-nav-rabbit-holes-${opt.id.toLowerCase()}`}
                            >
                              {visibleWeeks.map((week: RabbitHoleWeek) => {
                                const isActiveWeek =
                                  week.id === activeRabbitHoleWeekId;
                                return (
                                <button
                                  key={week.id}
                                  type="button"
                                  onClick={() => {
                                    setLanguageSpace(opt.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN");
                                    void navigate({
                                      to: "/app/rabbit_hole",
                                      search: { weekId: week.id, summary: undefined },
                                    });
                                  }}
                                  className="w-full rounded-md px-2 py-1 text-left transition hover:bg-slate-50"
                                  style={{ backgroundColor: isActiveWeek ? "#F1F5F9" : "transparent" }}
                                  data-testid={`side-nav-rabbit-hole-${opt.id.toLowerCase()}-${week.id}`}
                                >
                                  <span className="block truncate text-xs leading-4 text-slate-600">
                                    {week.topic}
                                  </span>
                                  <span className="block truncate text-[10px] leading-3 text-slate-400">
                                    {week.week_start} – {week.week_end}
                                  </span>
                                </button>
                                );
                              })}
                              {hiddenCount > 0 ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowAllWeeksForLanguage((prev) => {
                                      const next = new Set(prev);
                                      next.add(opt.id);
                                      return next;
                                    })
                                  }
                                  className="w-full rounded-md px-2 py-1 text-left text-xs text-slate-400 hover:text-slate-600 transition"
                                  data-testid={`side-nav-rabbit-holes-show-more-${opt.id.toLowerCase()}`}
                                >
                                  {translation.translate(
                                    "side_nav_show_more_weeks",
                                    { count: hiddenCount },
                                  )}
                                </button>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    {!hasAddedAllEnabledSpaces ? (
                      <button
                        type="button"
                        data-testid="side-nav-add-language-space"
                        onClick={openAddLanguageSpaceFlow}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-left text-slate-700 transition hover:bg-slate-50"
                      >
                        <PlusIcon className="h-4 w-4 text-slate-500" />
                        <span className="text-sm font-medium leading-5">
                          {translation.translate("space_settings_add_new_space")}
                        </span>
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="space-y-0.5">
                {(
                  [
                  {
                    link: "/",
                    text: "uxhulu.com",
                    pageTitle: translation.translate("sidenav_title_homepage"),
                    icon: <ArrowUpRightIcon className="w-5 h-5" />,
                  } as NavItem,
                  show_experimental
                    ? ({
                        link: "/app/account",
                        text: "Bark Account",
                        pageTitle: translation.translate("sidenav_title_account"),
                        icon: <BanknotesIcon className="w-5 h-5" />,
                      } as NavItem)
                    : undefined,
                  {
                    link: "/app/vocabulary_progress",
                    text: translation.translate("link_vocabulary_progress"),
                    icon: <ChartBarIcon className="w-5 h-5" />,
                  } as NavItem,
                  {
                    link: "/app/rabbit_hole",
                    text: translation.translate("sidenav_rabbit_hole"),
                    icon: <SparklesIcon className="w-5 h-5" />,
                  } as NavItem,
                  {
                    link: "/app/product_logbook",
                    text: translation.translate("sidenav_product_updates"),
                    icon: <InformationCircleIcon className="w-5 h-5" />,
                  } as NavItem,
                  userData.is_staff
                    ? ({
                        link: "/app/technical_change_log",
                        text: translation.translate(
                          "sidenav_technical_change_log",
                        ),
                        icon: <CommandLineIcon className="w-5 h-5" />,
                      } as NavItem)
                    : undefined,
                  show_experimental
                    ? ({
                        link: "/app/vocabulary_trainer_new",
                        text: "New Trainer",
                        pageTitle: "New Trainer",
                        icon: <AcademicCapIcon className="w-5 h-5" />,
                      } as NavItem)
                    : undefined,
                  {
                    link: "/app/profile",
                    text: translation.translate("profile_title"),
                    icon: <UserIcon className="h-5 w-5" />,
                    testId: "desktop-side-nav-profile-link",
                  } as NavItem,
                  {
                    link: "/app/settings",
                    text: translation.translate("sidenav_settings"),
                    icon: <Cog8ToothIcon className="w-5 h-5" />,
                  } as NavItem,
                ] as (NavItem | undefined)[]
              )
                .filter((obj): obj is NavItem => obj !== undefined)
                .map((obj) => (
                  <Link
                    key={obj.link}
                    to={obj.link}
                    data-testid={obj.testId}
                    aria-label={obj.text}
                    title={obj.text}
                    onClick={closeNavbarOnMobile}
                  >
                    <div
                      className="flex items-start gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                    >
                      <span className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center text-slate-500">
                        {obj.icon}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate">{obj.text}</p>
                        {obj.pageTitle && obj.pageTitle !== obj.text ? (
                          <p className="truncate text-[11px] font-medium leading-4 text-slate-500">
                            {obj.pageTitle}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </Link>
                ))}
                </div>
                <div
                  data-testid="side-nav-alpha-section"
                  className="space-y-0.5 border-t border-slate-200 pt-2"
                >
                {desktopSidebarExpanded ? (
                  <div className="px-3 py-1">
                    <p className="text-[11px] font-medium leading-4 text-slate-500">
                      {translation.translate("sidenav_alpha_version")}
                    </p>
                    <p className="mt-0.5 text-[11px] leading-4 text-slate-500">
                      {translation.translate("sidenav_alpha_tools_hint")}
                    </p>
                  </div>
                ) : null}
                <Link
                  to="/app/quick_translate"
                  data-testid="desktop-side-nav-quick-translate-link"
                  aria-label={translation.translate("quick_translate_label")}
                  title={translation.translate("quick_translate_label")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <PencilSquareIcon className="h-4 w-4 text-slate-500" />
                    <span>{translation.translate("quick_translate_label")}</span>
                  </div>
                </Link>
                {desktopSidebarExpanded ? (
                  <div className="px-3 py-2">
                    <ProgressBar />
                  </div>
                ) : null}
                {PATTERN_UNLOCKS_MVP_VISIBLE ? (
                  <Link
                    to="/app/language_insights"
                    data-testid="language-insights-alpha-side-nav-link"
                    aria-label={translation.translate("sidenav_language_insights")}
                    title={translation.translate("sidenav_language_insights")}
                    onClick={closeNavbarOnMobile}
                  >
                    <div
                      className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                    >
                      <LanguageIcon className="h-4 w-4 text-slate-500" />
                      <span>
                        {translation.translate("sidenav_language_insights")}
                      </span>
                    </div>
                  </Link>
                ) : null}
                <Link
                  to="/app/vocabulary_trainer"
                  data-testid="vocabulary-trainer-alpha-side-nav-link"
                  aria-label={translation.translate("link_vocabulary_list")}
                  title={translation.translate("link_vocabulary_list")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <ClipboardDocumentListIcon className="h-4 w-4 text-slate-500" />
                    <span>
                      {translation.translate("link_vocabulary_list")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/interest_map"
                  data-testid="interest-map-alpha-side-nav-link"
                  aria-label={translation.translate("sidenav_interest_map")}
                  title={translation.translate("sidenav_interest_map")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <MapIcon className="h-4 w-4 text-slate-500" />
                    <span>
                      {translation.translate("sidenav_interest_map")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/vocabulary_composer_mockup"
                  data-testid="vocabulary-composer-mockup-side-nav-link"
                  aria-label={translation.translate("sidenav_vocabulary_composer_mockup")}
                  title={translation.translate("sidenav_vocabulary_composer_mockup")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <PencilSquareIcon className="h-4 w-4 text-slate-500" />
                    <span>
                      {translation.translate("sidenav_vocabulary_composer_mockup")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/test_dashboard"
                  data-testid="test-dashboard-alpha-side-nav-link"
                  aria-label={translation.translate("sidenav_test_dashboard")}
                  title={translation.translate("sidenav_test_dashboard")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <BeakerIcon className="h-4 w-4 text-slate-500" />
                    <span>
                      {translation.translate("sidenav_test_dashboard")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/staff_console"
                  data-testid="staff-console-alpha-side-nav-link"
                  aria-label={translation.translate("sidenav_staff_console")}
                  title={translation.translate("sidenav_staff_console")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <ChartBarIcon className="h-4 w-4 text-slate-500" />
                    <span>
                      {translation.translate("sidenav_staff_console")}
                    </span>
                  </div>
                </Link>
                <Link
                  to="/app/dialogue"
                  aria-label={translation.translate("sidenav_speaking_practice")}
                  title={translation.translate("sidenav_speaking_practice")}
                  onClick={closeNavbarOnMobile}
                >
                  <div
                    className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                  >
                    <MicrophoneIcon className="h-4 w-4 text-slate-500" />
                    <span>
                      {translation.translate("sidenav_speaking_practice")}
                    </span>
                  </div>
                </Link>
              </div>
            {userData.is_staff && (
              <a
                key="/admin"
                href="/admin"
                aria-label={translation.translate("sidenav_admin_area")}
                title={translation.translate("sidenav_admin_area")}
              >
                <div
                  className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
                >
                  <KeyIcon className="h-4 w-4 text-slate-500" />
                  <span>
                    {translation.translate("sidenav_admin_area")}
                  </span>
                </div>
              </a>
            )}
              <div
              data-testid="logout-button"
              aria-label={translation.translate("logout_button")}
              title={translation.translate("logout_button")}
              className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium leading-5 text-slate-700 transition hover:bg-slate-50"
              onClick={() => login.logoutMutation.mutate()}
            >
              <ArrowRightStartOnRectangleIcon className="h-4 w-4 text-slate-500" />
              <span>
                {translation.translate("logout_button")}
              </span>
            </div>
              </div>
            </div>
          </div>
        ) : null}
        <div
          data-app-content-scroll
          className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${
            showMobileBottomNav
              ? "pb-[calc(6.5rem+env(safe-area-inset-bottom))] md:pb-0"
              : ""
          }`}
          style={
            showMobileLanguagePageNavigation
              ? { backgroundColor: "transparent" }
              : { backgroundColor: "#fafafa" }
          }
        >
          <Outlet />
        </div>
      </div>
      {showMobileBottomNav ? (
        <div
          className="fixed inset-x-0 bottom-0 z-40 flex items-end justify-center gap-3 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:hidden"
          data-testid="mobile-bottom-nav"
        >
          <div className="flex h-[58px] min-w-0 items-center gap-1 rounded-[28px] border border-slate-200 bg-white px-2 py-2">
            {mobileBottomNavItems.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={item.onClick}
                aria-label={item.label}
                data-testid={item.testId}
                className={`inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full p-0 transition-colors ${
                  item.active
                    ? "bg-slate-100 text-slate-950"
                    : "text-slate-500"
                }`}
              >
                {item.icon}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              void navigate({
                to: "/app/reeprise",
                search: { mobileView: "create" },
              });
            }}
            aria-label={translation.translate("mobile_nav_create")}
            data-testid="mobile-bottom-nav-create"
            className={`inline-flex h-[58px] w-[58px] min-h-[58px] min-w-[58px] shrink-0 aspect-square items-center justify-center rounded-full border p-0 leading-none transition-colors ${
              isOnMobileCreateView
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white text-slate-950"
            }`}
          >
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      ) : null}
      <SnackBar />
    </div>
  );
}
